import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "../../../utils/apiClient";
import { fetchStudentPortfolio } from "../../Portfolio/portfolioApi";

/**
 * One thing the page reads from the API: its data, whether it is loading, and
 * the error if it failed.
 *
 * `reload()` refetches but KEEPS the last data on screen, so refreshing after a
 * payment does not flash every card back to a skeleton. A new `key` (another
 * student) drops it instead — the old student's numbers must never show under
 * the new one's name.
 */
export function useResource(key, load, enabled = true) {
  const [state, setState] = useState({ data: null, loading: enabled, error: null });
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  const keyRef = useRef(key);

  useEffect(() => {
    const keyChanged = keyRef.current !== key;
    keyRef.current = key;
    if (!enabled) {
      setState({ data: null, loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ data: keyChanged ? null : s.data, loading: true, error: null }));
    loadRef.current().then(
      (data) => { if (!cancelled) setState({ data, loading: false, error: null }); },
      (error) => { if (!cancelled) setState((s) => ({ data: s.data, loading: false, error })); },
    );
    return () => { cancelled = true; };
  }, [key, enabled, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}

const loadStudent = async (studentId) => {
  const { data } = await apiClient.get(`/students/${studentId}`);
  const student = { ...data.student };
  // A photo in private storage arrives as a key, not a URL.
  if (student.profilePhoto && !/^https?:\/\//.test(student.profilePhoto)) {
    try {
      const { data: file } = await apiClient.get("/files/presign-get", { params: { key: student.profilePhoto } });
      student.profilePhoto = file.url;
    } catch {
      student.profilePhoto = "";
    }
  }
  return { student, courseAssignments: data.courseAssignments || [] };
};

/**
 * Everything the student record reads up front, fetched in parallel.
 *
 * Each resource fails on its own: a plan without fee management answers the
 * fee calls with a 403, and that must cost the page its Fees tab, not the whole
 * record. Only `record` — the student themselves — is required to render.
 *
 * Tabs that page through history (attendance, mock interviews) fetch their own,
 * when they are opened.
 */
export default function useStudentRecord(studentId, { feesEnabled = false, interviewsEnabled = false } = {}) {
  const record = useResource(studentId, () => loadStudent(studentId));
  const stats = useResource(studentId, () =>
    apiClient.get(`/dashboard/student/${studentId}`).then((r) => r.data));
  const plans = useResource(studentId, () =>
    apiClient.get(`/fees/plans/student/${studentId}`).then((r) => r.data.plans || []), feesEnabled);
  const payments = useResource(studentId, () =>
    apiClient.get(`/fees/student/${studentId}`).then((r) => r.data.transactions || []), feesEnabled);
  const portfolio = useResource(studentId, () => fetchStudentPortfolio(studentId));
  const interviews = useResource(studentId, () =>
    apiClient.get(`/interviews/student/${studentId}`).then((r) => r.data.interviews || []), interviewsEnabled);

  return { record, stats, plans, payments, portfolio, interviews };
}
